"use client";

import { useEffect, useRef } from "react";
import { EllipsisVertical } from "lucide-react";

type RideMode = "ride_now" | "destination" | "explore" | "scheduled";
type Props = { onSelect: (mode: RideMode) => void; currentMode?: RideMode; inline?: boolean };
const choices = [
  ["destination", "Search Existing Ride"],
  ["explore", "Nearby Rides"],
  ["scheduled", "Scheduled Request"],
] as const;

export default function RideModeMenu({ onSelect, currentMode, inline = false }: Props) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    function outside(event: MouseEvent) {
      const details = detailsRef.current;
      if (details && !details.contains(event.target as Node)) details.open = false;
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);
  return <details suppressHydrationWarning ref={detailsRef} className={inline ? "group" : "group relative self-stretch"}
    onKeyDown={event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.currentTarget.open = false;
        event.currentTarget.querySelector("summary")?.focus();
      }
    }}>
    <summary aria-label="Other ride options" title="Other ride options"
      className="flex h-full min-h-10 w-fit cursor-pointer list-none items-center justify-center rounded-xl px-2 text-indigo-700 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 [&::-webkit-details-marker]:hidden">
      <EllipsisVertical className="h-5 w-5" />
    </summary>
    <div className={inline ? "mt-2 space-y-1 rounded-xl border border-slate-200 bg-white p-1" : "absolute right-0 top-full z-[100] mt-2 w-56 space-y-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"}>
      {choices.map(([mode, label]) => <button key={mode} type="button" aria-pressed={currentMode === mode}
        onClick={() => { if (detailsRef.current) detailsRef.current.open = false; onSelect(mode); }}
        className="block w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600">
        {label}
      </button>)}
    </div>
  </details>;
}
